import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowRight, Mail, MapPin, Linkedin, Globe, CheckCircle2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATTER_TYPES = [
  'Family law / divorce',
  'Probate / estate',
  'Bankruptcy',
  'Insurance claim',
  'Litigation / discovery',
  'Other',
];

export default function ContactPage() {
  const topRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Contact VARfacts';
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    organization: '',
    email: '',
    matter_type: '',
    description: '',
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch {
      toast.error('Failed to send. Please try again or email secure.intake@varfacts.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="contact-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#D4D4D0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <img src="/10_VARfacts_logo_header_400x100.png" alt="VARfacts" className="h-8" />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/virtual-asset-registry-framework" className="nav-link text-sm">Framework</Link>
            <Link to="/family-law-digital-assets" className="nav-link text-sm">Family Law</Link>
            <Link to="/estate-digital-asset-records" className="nav-link text-sm">Estates</Link>
            <Link to="/contact" className="nav-link text-sm">Contact</Link>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild className="btn-gold px-5 py-2 rounded-sm text-xs">
              <a href="mailto:secure.intake@varfacts.com">Contact Us</a>
            </Button>
          </div>
        </div>
      </nav>

      <div className="pt-16" ref={topRef}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
          {submitted ? (
            <div className="max-w-xl mx-auto text-center space-y-6" data-testid="contact-success">
              <div className="w-16 h-16 mx-auto bg-[#0B1F3F] rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[#C5A55A]" />
              </div>
              <h1 className="font-heading text-3xl font-semibold text-[#0B1F3F]">Submission Received</h1>
              <p className="text-[#64748B] leading-relaxed">
                VARfacts responds within one business day. All inquiries are confidential.
              </p>
              <p className="text-sm text-[#94A3B8]">Contact: secure.intake@varfacts.com</p>
              <Button asChild className="btn-primary-varfacts px-6 py-3 rounded-sm">
                <Link to="/">Return to Home</Link>
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-7 space-y-8">
                <div className="space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A55A]">Contact</p>
                  <h1 className="font-heading text-4xl sm:text-5xl text-[#0B1F3F] leading-tight" data-testid="contact-h1">
                    Submit a Matter for Evaluation
                  </h1>
                  <div className="w-16 h-0.5 bg-[#C5A55A]" />
                  <p className="text-[#64748B] leading-relaxed">
                    No keys, credentials, or custody required. Just the produced record set.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Name *</Label>
                    <Input
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Firm or Organization *</Label>
                    <Input
                      data-testid="contact-org-input"
                      value={form.organization}
                      onChange={(e) => handleChange('organization', e.target.value)}
                      className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Email *</Label>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Matter Type *</Label>
                    <select
                      data-testid="contact-matter-type"
                      value={form.matter_type}
                      onChange={(e) => handleChange('matter_type', e.target.value)}
                      className="w-full h-12 px-3 bg-white border border-[#D4D4D0] rounded-md text-sm text-[#0B1F3F] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F] focus:outline-none"
                      required
                    >
                      <option value="" disabled>Select matter type</option>
                      {MATTER_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Brief description of the record situation</Label>
                    <Textarea
                      data-testid="contact-description-input"
                      value={form.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Optional — describe the record set and what you need evaluated."
                      className="min-h-[120px] bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
                    />
                  </div>
                  <Button
                    data-testid="contact-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="btn-primary-varfacts px-8 py-3 rounded-sm h-12"
                  >
                    {submitting ? 'Submitting...' : 'Request an Intake Outline'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                  <p className="text-xs text-[#94A3B8] leading-relaxed">
                    VARfacts responds within one business day. All inquiries are confidential.<br />
                    Contact: <a href="mailto:secure.intake@varfacts.com" className="hover:text-[#0B1F3F]">secure.intake@varfacts.com</a>
                  </p>
                </form>
              </div>

              <div className="lg:col-span-5 space-y-6 lg:pt-16">
                <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-4">
                  <h3 className="font-heading text-lg text-[#0B1F3F] font-semibold">VARfacts</h3>
                  <Separator className="bg-[#D4D4D0]" />
                  <div className="space-y-3 text-sm text-[#64748B]">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-[#C5A55A]" />
                      <a href="mailto:secure.intake@varfacts.com" className="hover:text-[#0B1F3F]">secure.intake@varfacts.com</a>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-[#C5A55A]" />
                      <span>Nashville, Tennessee</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe className="w-4 h-4 text-[#C5A55A]" />
                      <span>varfacts.com</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Linkedin className="w-4 h-4 text-[#C5A55A]" />
                      <a href="https://www.linkedin.com/company/varfacts" target="_blank" rel="noopener noreferrer" className="hover:text-[#0B1F3F]">
                        linkedin.com/company/varfacts
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <footer className="bg-[#0B1F3F] py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5 space-y-4">
              <img src="/01_VARfacts_logo_white_transparent.png" alt="VARfacts" className="h-8 opacity-90" />
            </div>
            <div className="md:col-span-3 space-y-3">
              <p className="text-white/40 text-xs font-semibold uppercase tracking-widest">Links</p>
              <div className="space-y-2">
                <Link to="/" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Home</Link>
                <Link to="/virtual-asset-registry-framework" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Framework</Link>
                <Link to="/family-law-digital-assets" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Family Law</Link>
                <Link to="/estate-digital-asset-records" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Estates</Link>
                <Link to="/contact" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Contact</Link>
              </div>
            </div>
            <div className="md:col-span-4 space-y-3">
              <p className="text-white/40 text-xs font-semibold uppercase tracking-widest">Contact</p>
              <div className="space-y-2">
                <p className="text-white/60 text-sm">secure.intake@varfacts.com</p>
                <p className="text-white/60 text-sm">Nashville, Tennessee</p>
                <a href="https://www.linkedin.com/company/varfacts" target="_blank" rel="noopener noreferrer" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">LinkedIn</a>
              </div>
            </div>
          </div>
          <Separator className="my-8 bg-white/10" />
          <p className="text-white/20 text-xs">&copy; 2026 VARfacts. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}
